import { useState, useMemo } from "react";
import { DateTime } from "luxon";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import ActivityForm from "../../_form";
import { FormValues } from "../../_form/validation/schema";
import MultiStepFormSkeleton from "@/components/Skeleton/MultiStepFormSkeleton";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { useQuery } from "@apollo/client";
import { GET_ACTIVITY } from "../../../queries";
import { EDIT_ACTIVITY_MUTATION } from "../../_form/queries";
import { useMutation } from "@apollo/client";
import { RateUnit } from "../../../types";

function dateValueToISO(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = DateTime.fromISO(value, { zone: "utc" });
  return parsed.isValid ? (parsed.toISODate() ?? "") : value;
}

export default function EditActivityPage() {
  const location = useLocation();
  const { activityTypeId = "", activityId = "" } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [editActivityMutation] = useMutation(EDIT_ACTIVITY_MUTATION, {
    refetchQueries: [GET_ACTIVITY]
  });

  const activityFromState = location.state?.activity as
    | { id: string; title: string; startDate?: string; endDate?: string }
    | undefined;
  const { data, loading } = useQuery(GET_ACTIVITY, {
    variables: { id: activityId },
    skip: !!activityFromState
  });

  const activityData = activityFromState ?? data?.activity;

  const rateUnit = activityData?.activityType?.config?.creditRates?.[0]?.rateUnit;

  const initialValues: Partial<FormValues> | undefined = useMemo(() => {
    if (!activityData) return undefined;

    const startDate = dateValueToISO(activityData.allowParticipationStarting);
    const endDate = dateValueToISO(activityData.allowParticipationThrough);
    return {
      title: activityData.title,
      activityType: { id: activityData.activityType?.id ?? "", name: activityData.activityType?.title ?? "" },
      section: activityData.managingSection?.name ?? "",
      sectionId: activityData.managingSection?.id ?? "",
      activityDescription: activityData.internalDescription ?? "",
      duration: rateUnit === RateUnit.INSTANCE ? "1" : (activityData.duration?.toString() ?? ""),
      startDate: {
        startDate: startDate,
        endDate: startDate
      },
      endDate: {
        startDate: endDate,
        endDate: endDate
      },
      checkinEnabled: activityData.checkinEnabled ?? false
    };
  }, [activityData, rateUnit]);

  const handleSubmit = async (data: FormValues) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setErrorBanner(null);
    try {
      const { data: responseData } = await editActivityMutation({
        variables: {
          input: {
            activityId: activityId,
            activityType: data.activityType.name,
            title: data.title,
            description: data.activityDescription,
            managingSectionId: data.sectionId,
            duration: data.duration ? parseFloat(data.duration) : null,
            ...(data.startDate?.startDate && { allowParticipationStarting: data.startDate?.startDate }),
            allowParticipationThrough: data.endDate?.startDate || null,
            checkinEnabled: data.checkinEnabled
          }
        }
      });
      const { success, activity, message } = responseData.editActivity;
      if (success) {
        navigate(`/activities/activity-types/${activityTypeId}/activity/${activity.id}`);
        showSnackbar("Activity updated");
      } else {
        const errorMessage = message || "Failed to update activity";
        setErrorBanner(errorMessage);
        showErrorSnackbar(errorMessage);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update activity";
      setErrorBanner(errorMessage);
      showErrorSnackbar(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!activityData && !loading) {
    return (
      <div className="p-6">
        <div className="text-title-large">Activity not found</div>
      </div>
    );
  }

  if (!activityData) {
    return <MultiStepFormSkeleton stepCount={3} renderStepper={true} formSections={3} />;
  }

  return (
    <ActivityForm
      onSubmit={handleSubmit}
      initialValues={initialValues}
      isEditing={true}
      isSubmitting={isSubmitting}
      errorBanner={errorBanner ?? undefined}
      setErrorBanner={(banner) => setErrorBanner(banner ?? null)}
      rateUnit={rateUnit}
      totalParticipations={activityData?.participations?.totalCount}
    />
  );
}
